import { Dispatch, SetStateAction, useEffect } from 'react'
import { useFeature } from '@growthbook/growthbook-react'
import { ActivityRating, StudentWithScores, Score, TaskRating, SavingCallbacks } from 'contexts/assessment'
import { RectangleButton } from 'components/buttons'
import { ActivityDetails } from './ActivityDetails'
import { getDisplayName, getSameOriginPhotoUrl } from 'utils/get-display-name'
import { useSaveEncounterResults } from './useSaveEncounterResults'
import { calculateFinalScore, hasMinimumRatingsGiven } from './assessment-score-helpers'
import {
    Avatar,
    ButtonsWrapper,
    Wrapper,
    StudentGrades,
    Grade,
    GradeWrapper,
    SpacingWrapper,
    StudentsSection,
    StudentWrapper,
    StudentCommentInputField,
    StudentDetails
} from './ActivityPanelStyle'

type ActivityPanelProps = {
    setCurrentTab: Dispatch<SetStateAction<'activity' | 'comments' | 'summary'>>
    currentTab: string
    activitiesRatings: ActivityRating[]
    students: StudentWithScores[]
    currentActivityIndex: number
    setCurrentActivityIndex: Dispatch<SetStateAction<number>>
    score?: Score
    setScore: Dispatch<SetStateAction<Score | undefined>>
    setSavingStatus: () => SavingCallbacks
    tasksRatings: TaskRating[]
    isClassClosed: boolean
}
const ActivityPanel: React.FC<ActivityPanelProps> = props => {
    const {
        setScore,
        students,
        setCurrentTab,
        currentTab,
        activitiesRatings,
        currentActivityIndex,
        setCurrentActivityIndex,
        setSavingStatus,
        tasksRatings,
        isClassClosed
    } = props
    const currentActivity = activitiesRatings[currentActivityIndex]
    const lastActivity = currentActivityIndex === activitiesRatings.length - 1
    const saveResults = useSaveEncounterResults(students, setSavingStatus)
    const isNewAssessmentApiEnabled = useFeature('goc-assessment-api-v2').value === true

    // Function to move to the next activity or tab
    const moveToNextActivityOrTab = (): void => {
        if (lastActivity) {
            if (currentTab === 'activity') {
                setCurrentTab('comments')
            } else {
                setCurrentTab('summary')
            }
        } else {
            setCurrentActivityIndex(prev => ++prev)
        }
    }
    /**
     * handleCommentChange.
     * @param {string} comment - Comments given by teacher.
     * @param {Object} student - The Current student's object.
     */
    const handleCommentChange = (comment: string, student: StudentWithScores): void => {
        student.comment = comment
        void saveResults(true, student)
    }

    /**
     * handleGradeChange.
     * @param {HTMLElement} target - Html element value.s
     * @param {Object} student - The Current student's object.
     * @param {Object} rating - The Score rating for the activities.
     */
    const handleGradeChange = (target: HTMLElement, student: StudentWithScores, rating: TaskRating): void => {
        const scoreValue = student.scores.get(rating.rating.id) === +target.innerHTML ? 0 : +target.innerHTML
        setScore({ value: scoreValue, rating: rating.rating, student })
        student.isScoreOrResultChanged = true
        student.isScoreAndResultAvailableInAPI = false
    }

    useEffect(() => {
        return () => {
            students.forEach(s => {
                if (isNewAssessmentApiEnabled) {
                    // NEW: Flexible minimum ratings logic
                    s.finalScore = calculateFinalScore(tasksRatings.map(task =>
                        s.scores.get(task.rating.id)
                    ))
                    s.isGraded = hasMinimumRatingsGiven(s.scores, tasksRatings)
                } else {
                    // OLD: Strict all-ratings-required logic
                    const currentStudentScores = [...s.scores.values()]
                    s.isGraded = s.scores.size === tasksRatings.length && !currentStudentScores.includes(0)
                    if (s.isGraded && currentStudentScores.length > 0) {
                        const sum = currentStudentScores.reduce((acc, score) => acc + score, 0)
                        s.finalScore = Math.round((sum / (tasksRatings.length * 4)) * 100)
                    }
                }
            })
        }
    }, [isNewAssessmentApiEnabled, students, tasksRatings])

    return (
        <Wrapper>
            <SpacingWrapper>
                <ActivityDetails currentActivity={currentActivity} />
                <StudentsSection>
                    {students.map(s => (
                        <StudentWrapper key={s.userId}>
                            <StudentDetails>
                                <Avatar sx={{ backgroundImage: s.photoUris.medium ? `url('${getSameOriginPhotoUrl(s.photoUris.medium)}')` : 'none' }}>
                                    {!s.photoUris.medium ? (
                                        <span id='participant-initials'>{getDisplayName(s, 'initials')}</span>
                                    ) : null}
                                </Avatar>
                                <p>{getDisplayName(s, 'student-name')}</p>
                            </StudentDetails>
                            {currentTab === 'comments' ? (
                                <StudentCommentInputField
                                    onBlur={e => handleCommentChange(e.target.value, s)}
                                    defaultValue={s.comment}
                                    multiline
                                    maxRows={4}
                                    minRows={1}
                                    disabled={s.isActivitiesCommentsDisabled || isClassClosed}
                                    isDisabled={s.isActivitiesCommentsDisabled || isClassClosed}
                                />
                            ) : (
                                <StudentGrades lastActivity={lastActivity}>
                                    {currentActivity.ratings.map(r => (
                                        <GradeWrapper key={r.rating.id} lastActivity={lastActivity}>
                                            {lastActivity ? (
                                                <p>
                                                    <span>{currentActivity.number}</span>
                                                    {r.description}
                                                </p>
                                            ) : null}
                                            <Grade
                                                onClick={e => s.isActivitiesCommentsDisabled || isClassClosed ? {}
                                                    : handleGradeChange(e.target as HTMLElement, s, r)}
                                                grade={s.scores.get(r.rating.id)}
                                                isDisabled={s.isActivitiesCommentsDisabled || isClassClosed}
                                            >
                                                <span>1</span>
                                                <span>2</span>
                                                <span>3</span>
                                                <span>4</span>
                                            </Grade>
                                        </GradeWrapper>
                                    ))}
                                </StudentGrades>
                            )}
                        </StudentWrapper>
                    ))}
                </StudentsSection>
            </SpacingWrapper>
            <ButtonsWrapper>
                <RectangleButton
                    onClick={() => setCurrentActivityIndex(prev => --prev)}
                    text='Previous Activity' color='error' variant='outlined'
                    disabled={currentActivityIndex === 0}
                />
                <RectangleButton
                    onClick={moveToNextActivityOrTab}
                    text='Next'
                    color='error'
                />
            </ButtonsWrapper>
        </Wrapper>
    )
}

export { ActivityPanel }
